import { Button, Col, Modal, Row } from 'antd';
import * as React from 'react';
import injectSheet from 'react-jss';
import { connect, ConnectedProps } from 'react-redux';
import { bindActionCreators } from 'redux';
import { OrgTypes } from 'src/Library/Constants';
import { StylesProps } from 'src/Library/Types';
import { useGenericState } from 'src/Library/UseGenericState';
import GetFinancingNowForm from './GetFinancingNowForm';
import { tokens } from 'src/design-system';

const ShipIcon = require('src/assets/images/ship.svg');
const InvoiceIcon = require('src/assets/images/invoice.svg');
const PaymentIcon = require('src/assets/images/payment.svg');

const styles = theme => ({
    container:{
        padding: '16px'
    },
    button: {
        marginRight: 8,
        '& .ant-btn': {
            borderColor: '#EF4277',
        },
        cursor: 'pointer'
    },
    buttonText: {
        color: '#EF4277',
        fontSize: '14px',
        fontWeight: 600
    },
    financingOptionsHeading: {
        textAlign: 'center',
        fontSize: '20px',
        fontWeight: 600
    },
    infoCard: {
        borderRadius: '8px',
        backgroundColor: '#F6F3FA',
        padding: '16px 32px',
        textAlign: 'center',
        display: 'flex',
        flexDirection: 'column'
    },
    cards: {
        marginTop: '16px',
    },
    infoHeading: {
        fontSize: '14px',
        color: '#663FA4',
    },
    infoText: {
        fontSize:'12px',
        lineHeight: '19px',
        fontWeight: 400,
        marginTop: '8px',
        color: tokens.colors.text
    },
    processText: {
        fontSize: '14px',
        fontWeight: 600,
        marginTop: '28px',
        color: tokens.colors.text
    },
    processListText: {
        fontSize: '12px',
        fontWeight: 400,
        marginTop: '12px',
        color: tokens.colors.textSecondary
    },
    process: {
        display: 'flex',
        flexDirection: 'column'
    },
    applyForLoanButton: {
        marginTop: '48px',
        textAlign: 'center'
    },
    applyForLoanButtonText: {
        fontSize: '14px',
        fontWeight: 400
    }
});

enum Steps {
    INFO = 'INFO',
    FORM = 'FORM'
}

interface State {
    showModal: boolean;
    currentStep: Steps;
}

interface IProps extends StylesProps<ReturnType<typeof styles>>, PropsFromRedux {}

const GetFinancingNowButton: React.FC<IProps> = (props) => {
    const [state, setState] = useGenericState<State>({
        showModal: false,
        currentStep: Steps.INFO
    });

    const { showModal, currentStep} = state;

    const {
        classes,
        userData,
        type,
        showGetFinancingButton
    } = props;

    React.useEffect(() => {
        let isGetFinancingModalTriggered: boolean | undefined = true;
        const storage = window.localStorage;
        if (storage) {
            isGetFinancingModalTriggered = !!storage.getItem('isGetFinancingModalTriggered');
            if (showGetFinancingButton && !isGetFinancingModalTriggered){
                toggleFormModalVisibility(true);
                storage.setItem('isGetFinancingModalTriggered', "true");
            }
        }
    }, []);

    const toggleFormModalVisibility = (selection: boolean = false) => {
        setState({
            showModal: selection
        });
    }
    
    const handleCloseClick = () => {
        const storage = window.localStorage;
        storage.setItem('isGetFinancingModalTriggered', "true");
        setState({
            showModal: false,
            currentStep: Steps.INFO
        });
    }

    const handleApplyForLoanClick = () => {
        setState({
            currentStep: Steps.FORM
        });
    }

    const renderFinancingForm = () => {
        return <GetFinancingNowForm
                />;
    }

    const renderFinancingInfo = () => {
        return (
            <div className={classes.container}>
                <div className={classes.financingOptionsHeading}>Financing options available:</div>
                <Row className={classes.cards} gutter={12}>
                    <Col span={12}>
                        <div className={classes.infoCard}>
                            <span className={classes.infoHeading}>
                                {type === OrgTypes.SHIPPER && 
                                    <span><img src={ShipIcon}/> Ship Now, Pay Later</span>
                                }
                                {type !== OrgTypes.SHIPPER && 
                                        <span><img src={PaymentIcon}/> Early Payment for Suppliers</span>
                                }
                            </span>
                            <span className={classes.infoText}>
                            Get instant, collateral free loans to pay your
                            shipping vendors and avail flexible 
                            repayment options.
                            </span>   
                        </div>
                    </Col>
                    <Col span={12}>
                        <div className={classes.infoCard}>
                            <span className={classes.infoHeading}>
                                    {type === OrgTypes.SHIPPER && 
                                        <span><img src={InvoiceIcon}/> Invoice Financing</span>
                                    }
                                    {type !== OrgTypes.SHIPPER && 
                                        <span><img src={InvoiceIcon}/> Freight Invoice Financing</span>
                                    }
                            </span> 
                            <span className={classes.infoText}>
                            Get instant payment on your outstanding export
                            invoices and improve your business’s
                            cash flow in the short term.
                            </span>                  
                        </div>
                    </Col>
                </Row>
                <div className={classes.process}>
                    <span className={classes.processText}>Process:</span>
                    <span className={classes.processListText}>
                    i.   Shipsy team guides you to get on boarded to the third party partner platform, fill the loan application 
                    and upload the required documents.
                    </span>
                    <span className={classes.processListText}>
                    ii.   3rd party partner verifies the documents and establishes the your credit eligibility.
                    </span>
                    <span className={classes.processListText}>
                    iii.   3rd party partner connects you with the right lender for loan disbursement.
                    </span>
                </div>
                <div className={classes.applyForLoanButton}>
                    <Button type='primary' onClick={() => handleApplyForLoanClick()}><span className={classes.applyForLoanButtonText}>Apply For Loan</span></Button>
                </div>
            </div>
        );
    }

    const renderFormModal = () => {
        return ( 
            <Modal
                width='688px'
                visible={showModal}
                closable={true}
                footer={null}
                onCancel={() => handleCloseClick()}
            >
                {currentStep === Steps.INFO && renderFinancingInfo()}
                {currentStep === Steps.FORM && renderFinancingForm()}
                
            </Modal>
        )
    }

    return (
        <>
        <div className={classes.button}>
            <Button onClick={() => toggleFormModalVisibility(true)}><span className={classes.buttonText}>Get Financing Now</span></Button>
        </div>
        {renderFormModal()}
        </>
    );
}

const mapStateToProps = ({ masterData }) => ({
  userData: masterData.userData,
  type : masterData.userData.type,
  showGetFinancingButton: masterData.show_get_financing_cta
});

const mapDispatchToProps = (dispatch) => bindActionCreators({
}, dispatch);

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(injectSheet(styles)(GetFinancingNowButton));
